import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import GeneratePage from './pages/GeneratePage';
import AdminPage from './pages/AdminPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import CoverGeneratorPage from './pages/CoverGeneratorPage';
import MetadataOptimizerPage from './pages/MetadataOptimizerPage';
import BookManagerPage from './pages/BookManagerPage';
import CreditPurchasePage from './pages/CreditPurchasePage';
import BookCreationWizard from './pages/BookCreationWizard';
import KDPGuidePage from './pages/KDPGuidePage';
import HelpCenterPage from './pages/HelpCenterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import RequestPasswordResetPage from './pages/RequestPasswordResetPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import SupportPage from './pages/SupportPage';
import MonetizeAIBooksPage from './pages/MonetizeAIBooksPage';
import MasterAmazonKDPWithAIPage from './pages/MasterAmazonKDPWithAIPage';
import BestKDPNiches2025Page from './pages/BestKDPNiches2025Page';
import PSEOPage from './pages/PSEOPage';
import UpgradePrompts, { TrialBanner } from './components/UpgradePrompts';
import LifetimeBanner from './components/LifetimeBanner';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-400"></div>
      </div>
    );
  }
  
  return user ? (
    <>
      <TrialBanner />
      <UpgradePrompts />
      {children}
    </>
  ) : <Navigate to="/login" />;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-400"></div>
      </div>
    );
  }
  
  return user && user.is_admin ? children : <Navigate to="/dashboard" />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/kdp-guide" element={<KDPGuidePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/request-password-reset" element={<RequestPasswordResetPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/monetize-ai-books.html" element={<MonetizeAIBooksPage />} />
      <Route path="/master-amazon-kdp-with-ai" element={<MasterAmazonKDPWithAIPage />} />
      <Route path="/best-kdp-niches-2025" element={<BestKDPNiches2025Page />} />
      <Route path="/payment-success" element={<PaymentSuccessPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><HelpCenterPage /></ProtectedRoute>} />
      <Route path="/generate" element={<ProtectedRoute><GeneratePage /></ProtectedRoute>} />
      <Route path="/create" element={<ProtectedRoute><BookCreationWizard /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      <Route path="/kdp-covers" element={<ProtectedRoute><CoverGeneratorPage /></ProtectedRoute>} />
      <Route path="/metadata-optimizer" element={<ProtectedRoute><MetadataOptimizerPage /></ProtectedRoute>} />
      <Route path="/books/:bookId" element={<ProtectedRoute><BookManagerPage /></ProtectedRoute>} />
      <Route path="/credits/purchase" element={<ProtectedRoute><CreditPurchasePage /></ProtectedRoute>} />
      
      {/* PSEO Routes with .html extension */}
      <Route path="/romance-book-cover-generator.html" element={<PSEOPage />} />
      <Route path="/how-to-write-romance-books.html" element={<PSEOPage />} />
      <Route path="/romance-book-book-creator.html" element={<PSEOPage />} />
      <Route path="/romance-book-publishing-guide.html" element={<PSEOPage />} />
      
      <Route path="/fantasy-book-cover-generator.html" element={<PSEOPage />} />
      <Route path="/how-to-write-fantasy-books.html" element={<PSEOPage />} />
      <Route path="/fantasy-book-book-creator.html" element={<PSEOPage />} />
      <Route path="/fantasy-book-publishing-guide.html" element={<PSEOPage />} />
      
      <Route path="/mystery-book-cover-generator.html" element={<PSEOPage />} />
      <Route path="/how-to-write-mystery-books.html" element={<PSEOPage />} />
      <Route path="/mystery-book-book-creator.html" element={<PSEOPage />} />
      <Route path="/mystery-book-publishing-guide.html" element={<PSEOPage />} />
      
      <Route path="/business-book-cover-generator.html" element={<PSEOPage />} />
      <Route path="/how-to-write-business-books.html" element={<PSEOPage />} />
      <Route path="/business-book-book-creator.html" element={<PSEOPage />} />
      <Route path="/business-book-publishing-guide.html" element={<PSEOPage />} />
      
      <Route path="/self-help-book-cover-generator.html" element={<PSEOPage />} />
      <Route path="/how-to-write-self-help-books.html" element={<PSEOPage />} />
      <Route path="/self-help-book-book-creator.html" element={<PSEOPage />} />
      <Route path="/self-help-book-publishing-guide.html" element={<PSEOPage />} />
      
      <Route path="/cookbook-book-cover-generator.html" element={<PSEOPage />} />
      <Route path="/how-to-write-cookbook-books.html" element={<PSEOPage />} />
      <Route path="/cookbook-book-book-creator.html" element={<PSEOPage />} />
      <Route path="/cookbook-book-publishing-guide.html" element={<PSEOPage />} />
      
      <Route path="/children-book-cover-generator.html" element={<PSEOPage />} />
      <Route path="/how-to-write-children-books.html" element={<PSEOPage />} />
      <Route path="/children-book-book-creator.html" element={<PSEOPage />} />
      <Route path="/children-book-publishing-guide.html" element={<PSEOPage />} />
      
      <Route path="/biography-book-cover-generator.html" element={<PSEOPage />} />
      <Route path="/how-to-write-biography-books.html" element={<PSEOPage />} />
      <Route path="/biography-book-book-creator.html" element={<PSEOPage />} />
      <Route path="/biography-book-publishing-guide.html" element={<PSEOPage />} />
      
      {/* Alias routes for alternative URL patterns */}
      <Route path="/romance-book-writing-guide.html" element={<PSEOPage />} />
      <Route path="/fantasy-book-writing-guide.html" element={<PSEOPage />} />
      <Route path="/mystery-book-writing-guide.html" element={<PSEOPage />} />
      <Route path="/business-book-writing-guide.html" element={<PSEOPage />} />
      <Route path="/self-help-book-writing-guide.html" element={<PSEOPage />} />
      <Route path="/cookbook-book-writing-guide.html" element={<PSEOPage />} />
      <Route path="/children-book-writing-guide.html" element={<PSEOPage />} />
      <Route path="/biography-book-writing-guide.html" element={<PSEOPage />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <div className="App">
            <LifetimeBanner />
            <AppRoutes />
            <Toaster 
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                background: '#1f2937',
                color: '#f9fafb',
                border: '1px solid #6366f1'
              }
            }}
          />
        </div>
      </Router>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;